import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Badge, Alert } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useGetLedgerAccountsByCustomer, useGetOpeningBalance } from '../../../services/loanService';
import { useGetCustomer } from '../../../services/customerService';
import { LedgerAccount, AccountStatus } from '../../../services/schemas/loanServiceSchemas';
import { formatDate, formatCurrency } from '../../../utils/formatters';
import { Tooltip } from 'react-tooltip';

const CustomerAccounts: React.FC = () => {
    const { customerNumber } = useParams<{ customerNumber: string }>();
    const [customerSummary, setCustomerSummary] = useState<{
        totalAccounts: number;
        dueAccounts: number;
        totalOutstanding: number;
        totalLoanAmount: number;
        customerName: string;
    } | null>(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Get ledger accounts for the customer
    const { data: accountsData, isLoading, error } = useGetLedgerAccountsByCustomer(
        { customerNumber: customerNumber || '' },
        {
            query: {
                enabled: !!customerNumber
            }
        }
    );

    const accounts = useMemo(() => (Array.isArray(accountsData) ? accountsData : []), [accountsData]);

    // Look up the customer's UUID (id) from their customerNumber so we can fetch opening balance
    const { data: customerData } = useGetCustomer(
        { customerNumber: customerNumber || '' },
        { query: { enabled: !!customerNumber } }
    ) as { data: any };
    const customerId: string | undefined = customerData?.id;

    // Fetch opening balance using the customer UUID
    const { data: openingBalanceData } = useGetOpeningBalance(
        customerId || '',
        { query: { enabled: !!customerId } }
    ) as { data: any };

    const filteredAccounts = useMemo(() => {
        if (!searchQuery.trim()) return accounts;
        const q = searchQuery.toLowerCase();
        return accounts.filter(a =>
            (a.accountNumber || '').toLowerCase().includes(q) ||
            (a.productCode || '').toLowerCase().includes(q) ||
            (a.customerName || '').toLowerCase().includes(q)
        );
    }, [accounts, searchQuery]);

    // Build the table data — prepend a synthetic opening balance row if one exists
    type TableRow = (LedgerAccount & { _isOpeningBalanceRow?: false }) | {
        _isOpeningBalanceRow: true;
        accountId: string;
        openingBalance: number;
        openingBalanceDate?: string;
        openingBalanceApplied?: boolean;
    };

    const tableData: TableRow[] = useMemo(() => {
        const rows: TableRow[] = [...filteredAccounts];
        if (openingBalanceData?.openingBalance !== undefined) {
            rows.unshift({
                _isOpeningBalanceRow: true,
                accountId: 'opening-balance-row',
                openingBalance: openingBalanceData.openingBalance,
                openingBalanceDate: openingBalanceData.openingBalanceDate,
                openingBalanceApplied: openingBalanceData.openingBalanceApplied,
            });
        }
        return rows;
    }, [filteredAccounts, openingBalanceData]);

    useEffect(() => {
        if (accounts.length > 0) {
            // Calculate base totals from accounts
            const baseOutstanding = accounts.reduce((sum, acc) => sum + ((acc as any).totalOutstanding || 0), 0);
            
            // Add opening balance to total outstanding ONLY if it has been applied
            const appliedOpeningBalance = (openingBalanceData?.openingBalanceApplied && openingBalanceData?.openingBalance) 
                ? openingBalanceData.openingBalance 
                : 0;
            
            const summary = {
                totalAccounts: accounts.length,
                dueAccounts: accounts.filter(acc => acc.status === 'IN_ARREARS' || acc.status === 'DEFAULTED').length,
                totalOutstanding: baseOutstanding + appliedOpeningBalance,
                totalLoanAmount: accounts.reduce((sum, acc) => sum + (acc.disbursedAmount || 0), 0),
                customerName: accounts[0]?.customerName || 'Unknown Customer'
            };
            setCustomerSummary(summary);
        }
    }, [accounts, openingBalanceData]);

    const getStatusBadge = (status: AccountStatus) => {
        const variants: { [key in AccountStatus]: { bg: string; text: string; icon: string } } = {
            'PENDING': { bg: 'primary', text: 'Pending', icon: 'fa fa-clock' },
            'ACTIVE': { bg: 'success', text: 'Active', icon: 'fa fa-check-circle' },
            'IN_ARREARS': { bg: 'warning', text: 'In Arrears', icon: 'fa fa-exclamation-triangle' },
            'DEFAULTED': { bg: 'danger', text: 'Defaulted', icon: 'fa fa-times-circle' },
            'PAID_OFF': { bg: 'info', text: 'Paid Off', icon: 'fa fa-check-double' },
            'WRITTEN_OFF': { bg: 'dark', text: 'Written Off', icon: 'fa fa-ban' },
            'CLOSED': { bg: 'secondary', text: 'Closed', icon: 'fa fa-lock' },
            'CANCELLED': { bg: 'secondary', text: 'Cancelled', icon: 'fa fa-times' }
        };

        const variant = variants[status] || { bg: 'secondary', text: status, icon: 'fa fa-circle' };
        return (
            <Badge bg={variant.bg} className="px-3 py-2 d-inline-flex align-items-center gap-1">
                <i className={`${variant.icon}`}></i>
                {variant.text}
            </Badge>
        );
    };

    const getStatusCount = (status: AccountStatus) => {
        return accounts.filter(acc => acc.status === status).length;
    };

    const columns = [
        {
            name: 'Account Number',
            selector: (row: TableRow) => ('accountNumber' in row ? row.accountNumber : '') || '',
            sortable: true,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) {
                    return (
                        <div className="d-flex align-items-center gap-2">
                            <div className="rounded p-2" style={{ backgroundColor: '#fff3cd', border: '1px solid rgba(255,193,7,0.3)' }}>
                                <i className="fa fa-money text-warning"></i>
                            </div>
                            <div>
                                <div className="fw-bold text-warning">Opening Balance</div>
                                {row.openingBalanceDate && (
                                    <small className="text-muted d-block">
                                        {formatDate(row.openingBalanceDate)}
                                    </small>
                                )}
                            </div>
                        </div>
                    );
                }
                const r = row as LedgerAccount;
                return (
                    <Link to={`/loan-accounts/${r.accountId}`} className="text-decoration-none">
                        <div className="d-flex align-items-center gap-2">
                            <div className="rounded p-2" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                <i className="fa fa-list-alt text-primary"></i>
                            </div>
                            <div>
                                <div className="fw-bold text-primary">{r.accountNumber || r.accountId.substring(0, 8)}</div>
                                <small className="text-muted">{r.productCode || 'N/A'}</small>
                            </div>
                        </div>
                    </Link>
                );
            },
            minWidth: '220px',
            wrap: true
        },
        {
            name: 'Loan Amount',
            selector: (row: TableRow) => ('disbursedAmount' in row ? row.disbursedAmount : 0) || 0,
            sortable: true,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) return <span className="text-muted">—</span>;
                return <div className="fw-semibold">{formatCurrency((row as LedgerAccount).disbursedAmount || 0)}</div>;
            },
            minWidth: '140px'
        },
        {
            name: 'Outstanding',
            selector: () => 0,
            sortable: false,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) {
                    return (
                        <div className="fw-bold text-info">
                            {formatCurrency(row.openingBalance)}
                        </div>
                    );
                }
                const outstanding = (row as any).totalOutstanding || 0;
                return (
                    <div className={outstanding > 0 ? 'fw-bold text-danger' : 'fw-semibold text-success'}>
                        {formatCurrency(outstanding)}
                    </div>
                );
            },
            minWidth: '140px'
        },
        {
            name: 'Status',
            selector: (row: TableRow) => ('status' in row ? row.status : '') || '',
            sortable: true,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) {
                    return (
                        <Badge bg={row.openingBalanceApplied ? 'success' : 'warning'} className="px-3 py-2 d-inline-flex align-items-center gap-1">
                            <i className={`fa ${row.openingBalanceApplied ? 'fa-check-circle' : 'fa-clock'}`}></i>
                            {row.openingBalanceApplied ? 'Applied' : 'Pending'}
                        </Badge>
                    );
                }
                return getStatusBadge((row as LedgerAccount).status);
            },
            minWidth: '130px'
        },
        {
            name: 'Commencement Date',
            selector: (row: TableRow) => ('disbursementDate' in row ? row.disbursementDate : '') || '',
            sortable: true,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) return <span className="text-muted">—</span>;
                const r = row as LedgerAccount;
                return (
                    <div>
                        <div>{formatDate(r.disbursementDate)}</div>
                        {r.firstDueDate && (
                            <small className="text-muted d-block">First due: {formatDate(r.firstDueDate)}</small>
                        )}
                    </div>
                );
            },
            minWidth: '180px',
            wrap: true
        },
        {
            name: 'Maturity Date',
            selector: (row: TableRow) => ('maturityDate' in row ? row.maturityDate : '') || '',
            sortable: true,
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) return <span className="text-muted">—</span>;
                return <span>{formatDate((row as LedgerAccount).maturityDate)}</span>;
            },
            minWidth: '130px'
        },
        {
            name: 'Actions',
            cell: (row: TableRow) => {
                if (row._isOpeningBalanceRow) {
                    return (
                        <Link to={`${customerData?.type === 'INDIVIDUAL' ? '/individual-customers' : '/loan-customers'}/${customerId}/opening-balance`} className="text-decoration-none">
                            <button
                                className="btn btn-sm btn-outline-warning rounded-pill"
                                data-tooltip-id="view-opening-balance"
                                style={{ minWidth: '80px' }}
                            >
                                <i className="fa fa-eye me-1"></i>
                                View
                            </button>
                            <Tooltip id="view-opening-balance" place="top" content="View Opening Balance Details" />
                        </Link>
                    );
                }
                const r = row as LedgerAccount;
                return (
                    <Link to={`/loan-accounts/${r.accountId}`} className="text-decoration-none">
                        <button
                            className="btn btn-sm btn-outline-primary rounded-pill"
                            data-tooltip-id={`view-account-${r.accountId}`}
                            style={{ minWidth: '80px' }}
                        >
                            <i className="fa fa-eye me-1"></i>
                            View
                        </button>
                        <Tooltip id={`view-account-${r.accountId}`} place="top" content="View Account Details" />
                    </Link>
                );
            },
            minWidth: '100px',
            center: true,
            allowOverflow: true
        }
    ];

    if (error) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <Alert variant="danger" className="rounded-3 shadow-sm border-0">
                        <div className="d-flex align-items-center">
                            <div className="flex-shrink-0">
                                <i className="fa fa-exclamation-circle fa-2x me-3"></i>
                            </div>
                            <div>
                                <h5 className="mb-1 fw-bold">Error Loading Customer Accounts</h5>
                                <p className="mb-0 text-muted">{(error as any)?.message || 'Failed to load customer accounts'}</p>
                            </div>
                        </div>
                    </Alert>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading customer accounts...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
            <div className="container-fluid">
                {/* Header Section */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb mb-2">
                                        <li className="breadcrumb-item"><Link to="/" className="text-decoration-none">Home</Link></li>
                                        <li className="breadcrumb-item"><Link to="/loan-accounts" className="text-decoration-none">Loan Accounts</Link></li>
                                        <li className="breadcrumb-item active">Customer Accounts</li>
                                    </ol>
                                </nav>
                                <h2 className="mb-0 fw-bold">Customer Accounts</h2>
                                {customerSummary && (
                                    <p className="text-dark mb-0 fw-semibold" style={{ fontSize: '1.05rem' }}>
                                        {customerSummary.customerName}
                                    </p>
                                )}
                            </div>
                            <Link to="/loan-accounts" className="btn btn-outline-secondary">
                                <i className="fa fa-arrow-left me-2"></i>
                                Back
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Summary Cards */}
                {customerSummary && (
                    <div className="row g-3 mb-4">
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Total Accounts</p>
                                            <h3 className="mb-0 fw-bold">{customerSummary.totalAccounts}</h3>
                                        </div>
                                        <div className="rounded-circle p-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(0,123,255,0.2)' }}>
                                            <i className="fa fa-list-alt fa-2x text-primary" style={{ display: 'block' }}></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Total Disbursed</p>
                                            <h3 className="mb-0 fw-bold text-primary">{formatCurrency(customerSummary.totalLoanAmount)}</h3>
                                        </div>
                                        <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                            <i className="fa fa-arrow-up fa-2x text-success"></i>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className={`border-0 shadow-sm h-100 ${customerSummary.totalOutstanding > 0 ? 'border-warning' : ''}`}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Total Outstanding</p>
                                            <h3 className={`mb-0 fw-bold ${customerSummary.totalOutstanding > 0 ? 'text-warning' : 'text-success'}`}>
                                                {formatCurrency(customerSummary.totalOutstanding)}
                                            </h3>
                                        </div>
                                        <div className={`bg-${customerSummary.totalOutstanding > 0 ? 'warning' : 'success'} bg-opacity-10 rounded-circle p-3`}>
                                            {customerSummary.totalOutstanding > 0 ? (
                                                <i className="fa fa-exclamation-triangle fa-2x text-warning" style={{ display: 'block' }}></i>
                                            ) : (
                                                <i className="fa fa-check-circle fa-2x text-success" style={{ display: 'block' }}></i>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-3 col-md-6">
                            <Card className={`border-0 shadow-sm h-100 ${customerSummary.dueAccounts > 0 ? 'border-danger' : 'border-success'}`}>
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div>
                                            <p className="text-muted small mb-1">Accounts Due</p>
                                            <h3 className={`mb-0 fw-bold ${customerSummary.dueAccounts > 0 ? 'text-danger' : 'text-success'}`}>
                                                {customerSummary.dueAccounts}
                                            </h3>
                                        </div>
                                        <div className={`bg-${customerSummary.dueAccounts > 0 ? 'danger' : 'success'} bg-opacity-10 rounded-circle p-3`}>
                                            {customerSummary.dueAccounts > 0 ? (
                                                <i className="fa fa-exclamation-circle fa-2x text-danger" style={{ display: 'block' }}></i>
                                            ) : (
                                                <i className="fa fa-check-circle fa-2x text-success" style={{ display: 'block' }}></i>
                                            )}
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>
                )}

                {/* Status Overview */}
                <div className="row g-3 mb-4">
                    <div className="col-12">
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-4">
                                <h5 className="mb-3 fw-bold">Account Status Overview</h5>
                                <div className="row g-3">
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 bg-success bg-opacity-10 rounded-3">
                                            <div className="h4 mb-1 fw-bold text-success">{getStatusCount('ACTIVE')}</div>
                                            <small className="text-muted">Active</small>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 bg-warning bg-opacity-10 rounded-3">
                                            <div className="h4 mb-1 fw-bold text-warning">{getStatusCount('IN_ARREARS')}</div>
                                            <small className="text-muted">In Arrears</small>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 bg-danger bg-opacity-10 rounded-3">
                                            <div className="h4 mb-1 fw-bold text-danger">{getStatusCount('DEFAULTED')}</div>
                                            <small className="text-muted">Defaulted</small>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 bg-info bg-opacity-10 rounded-3">
                                            <div className="h4 mb-1 fw-bold text-info">{getStatusCount('PAID_OFF')}</div>
                                            <small className="text-muted">Paid Off</small>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 rounded-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(108,117,125,0.2)' }}>
                                            <div className="h4 mb-1 fw-bold text-secondary">{getStatusCount('CLOSED')}</div>
                                            <small className="text-muted">Closed</small>
                                        </div>
                                    </div>
                                    <div className="col-lg-2 col-md-4 col-sm-6">
                                        <div className="text-center p-3 rounded-3" style={{ backgroundColor: '#f8f9fa', border: '1px solid rgba(33,37,41,0.2)' }}>
                                            <div className="h4 mb-1 fw-bold text-dark">{getStatusCount('WRITTEN_OFF')}</div>
                                            <small className="text-muted">Written Off</small>
                                        </div>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Search */}
                <div className="row mb-3">
                    <div className="col-12">
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="p-3">
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-0">
                                        <i className="fa fa-search text-muted"></i>
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-0 bg-light"
                                        placeholder="Search by account number, product, or customer name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </Card.Body>
                        </Card>
                    </div>
                </div>

                {/* Accounts Table */}
                <div className="row">
                    <div className="col-12">
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-white border-0 py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-bold">Accounts List</h6>
                                    <span className="text-muted small">{filteredAccounts.length} of {accounts.length} accounts</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <DataTable
                                    columns={columns as any}
                                    data={tableData}
                                    pagination
                                    highlightOnHover
                                    striped
                                    responsive
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                    paginationComponentOptions={{
                                        rowsPerPageText: 'Rows per page:',
                                        rangeSeparatorText: 'of',
                                    }}
                                    customStyles={{
                                        headCells: {
                                            style: {
                                                backgroundColor: '#f8f9fa',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px',
                                                paddingTop: '1rem',
                                                paddingBottom: '1rem',
                                                whiteSpace: 'nowrap',
                                                overflow: 'visible',
                                            },
                                        },
                                        cells: {
                                            style: {
                                                paddingTop: '1rem',
                                                paddingBottom: '1rem',
                                                overflow: 'visible',
                                            },
                                        },
                                    }}
                                    noDataComponent={
                                        <div className="text-center py-5">
                                            <i className="fa fa-inbox fa-3x text-muted mb-3"></i>
                                            <p className="text-muted mb-0">No accounts found matching your criteria</p>
                                        </div>
                                    }
                                />
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerAccounts;
